import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DollarSign, TrendingUp, Users, ShoppingCart, BarChart3, Download,
  AlertTriangle, Award, Share2, ArrowUpRight, ArrowDownRight, Repeat
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#059669", "#0d9488", "#0891b2", "#6366f1", "#ec4899", "#f59e0b"];

export default function CFODashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [revenueModelData, setRevenueModelData] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSettlements, setSelectedSettlements] = useState([]);
  const [batchNotes, setBatchNotes] = useState("");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [batchStatus, setBatchStatus] = useState("approved");
  const [batchStatusReason, setBatchStatusReason] = useState("");
  const [reviewReason, setReviewReason] = useState("");
  const [overrideBookingId, setOverrideBookingId] = useState("");
  const [overrideStatus, setOverrideStatus] = useState("on_hold");
  const [overrideReason, setOverrideReason] = useState("");
  const [adjustTargetType, setAdjustTargetType] = useState("settlement");
  const [adjustBookingId, setAdjustBookingId] = useState("");
  const [adjustBatchId, setAdjustBatchId] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("deduct");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [commissionForm, setCommissionForm] = useState({ target_type: "venue", segment: "all", tier: "all", commission_rate: "5", status: "active", notes: "" });
  const [subscriptionForm, setSubscriptionForm] = useState({ target_type: "venue", tier: "starter", billing_cycle: "monthly", subscription_fee: "1999", status: "active", notes: "" });
  const [specialRateForm, setSpecialRateForm] = useState({ target_type: "venue", segment: "all", tier: "all", promotion_code: "", commission_rate_override: "0", subscription_discount_pct: "0", status: "active", notes: "" });

  const activeTab = searchParams.get("tab") || "overview";
  const handleTabChange = (tab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    setSearchParams(params);
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, financeRes, revenueModelRes, ledgerRes] = await Promise.all([
        api.get("/pilot/analytics/cfo-dashboard"),
        api.get("/admin/finance/console"),
        api.get("/admin/revenue-model/config"),
        api.get("/admin/revenue-model/ledger"),
      ]);
      setData(overviewRes.data);
      setFinanceData(financeRes.data);
      setRevenueModelData(revenueModelRes.data);
      setLedgerData(ledgerRes.data);
    } catch (err) {
      toast.error("Failed to load CFO dashboard");
    } finally {
      setLoading(false);
    }
  };

  const exportBookings = async () => {
    try {
      const res = await api.get("/pilot/analytics/export/bookings");
      const blob = new Blob([JSON.stringify(res.data.bookings, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `setplay-bookings-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success(`Exported ${res.data.count} bookings`);
    } catch { toast.error("Export failed"); }
  };

  const exportRevenue = async () => {
    try {
      const res = await api.get("/pilot/analytics/export/revenue");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `setplay-revenue-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success("Revenue data exported");
    } catch { toast.error("Export failed"); }
  };

  const toggleSettlement = (bookingId) => {
    setSelectedSettlements((prev) => prev.includes(bookingId) ? prev.filter((id) => id !== bookingId) : [...prev, bookingId]);
  };

  const createBatch = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/admin/finance/batches", { booking_ids: selectedSettlements, notes: batchNotes });
      toast.success(`Approval request submitted: ${res.data.approval_request?.request_id || 'pending'}`);
      setSelectedSettlements([]);
      setBatchNotes("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Batch creation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const updateBatchStatus = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/finance/batches/${selectedBatchId}/status`, { status: batchStatus, reason: batchStatusReason });
      toast.success(`Approval request submitted: ${res.data.approval_request?.request_id || 'pending'}`);
      setBatchStatusReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Batch update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const exportBatch = async (batchId, format) => {
    try {
      const res = await api.get(`/admin/finance/batches/${batchId}/export`, {
        params: { format },
        responseType: format === "csv" ? "blob" : "json",
      });
      if (format === "json") {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${batchId}.json`;
        a.click();
        return;
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${batchId}.csv`;
      a.click();
    } catch {
      toast.error("Batch export failed");
    }
  };

  const reviewUrgentRequest = async (requestId, action) => {
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/finance/urgent-requests/${requestId}/review`, { action, reason: reviewReason });
      toast.success(`Approval request submitted: ${res.data.approval_request?.request_id || requestId}`);
      setReviewReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAdjustment = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/admin/finance/adjustments", {
        target_type: adjustTargetType,
        booking_id: adjustTargetType === "settlement" ? adjustBookingId : undefined,
        payout_batch_id: adjustTargetType === "payout_batch" ? adjustBatchId : undefined,
        adjustment_type: adjustmentType,
        amount: Number(adjustAmount),
        reason: adjustReason,
      });
      toast.success(`Approval request submitted: ${res.data.approval_request?.request_id || 'pending'}`);
      setAdjustBookingId("");
      setAdjustBatchId("");
      setAdjustAmount("");
      setAdjustReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Adjustment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitOverride = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/finance/settlements/${overrideBookingId}/override`, { status: overrideStatus, reason: overrideReason });
      toast.success(`Approval request submitted: ${res.data.approval_request?.request_id || 'pending'}`);
      setOverrideBookingId("");
      setOverrideReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Override failed");
    } finally {
      setSubmitting(false);
    }
  };

  const createCommissionRule = async () => {
    try {
      await api.post("/admin/revenue-model/commission-rules", { ...commissionForm, commission_rate: Number(commissionForm.commission_rate) });
      toast.success("Commission rule saved");
      setCommissionForm({ target_type: "venue", segment: "all", tier: "all", commission_rate: "5", status: "active", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save commission rule");
    }
  };

  const createSubscriptionRule = async () => {
    try {
      await api.post("/admin/revenue-model/subscription-fees", { ...subscriptionForm, subscription_fee: Number(subscriptionForm.subscription_fee) });
      toast.success("Subscription fee rule saved");
      setSubscriptionForm({ target_type: "venue", tier: "starter", billing_cycle: "monthly", subscription_fee: "1999", status: "active", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save subscription fee rule");
    }
  };

  const createSpecialRate = async () => {
    try {
      await api.post("/admin/revenue-model/special-rates", {
        ...specialRateForm,
        commission_rate_override: Number(specialRateForm.commission_rate_override),
        subscription_discount_pct: Number(specialRateForm.subscription_discount_pct),
      });
      toast.success("Special rate / discount saved");
      setSpecialRateForm({ target_type: "venue", segment: "all", tier: "all", promotion_code: "", commission_rate_override: "0", subscription_discount_pct: "0", status: "active", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save special rate");
    }
  };

  const exportLedgerCsv = async () => {
    try {
      const res = await api.get("/admin/revenue-model/ledger/export", { params: { format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "setplay-central-ledger.csv";
      a.click();
    } catch {
      toast.error("Ledger export failed");
    }
  };

  const printLedgerPdf = () => {
    window.print();
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const { core_metrics: core, booking_metrics: bk, financial_metrics: fin, supply_metrics: supply, growth_metrics: growth, health_metrics: health } = data;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6" data-testid="cfo-dashboard-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl">CFO Dashboard</h1>
          <p className="text-muted-foreground text-sm">Pilot financial & operational overview</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportBookings} data-testid="export-bookings-btn">
            <Download className="w-4 h-4 mr-1" /> Bookings
          </Button>
          <Button variant="outline" size="sm" onClick={exportRevenue} data-testid="export-revenue-btn">
            <Download className="w-4 h-4 mr-1" /> Revenue
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full max-w-3xl grid-cols-4">
          <TabsTrigger value="overview" data-testid="cfo-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="finance" data-testid="cfo-tab-finance">Finance Console</TabsTrigger>
          <TabsTrigger value="revenue-model" data-testid="cfo-tab-revenue-model">Revenue Model</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="cfo-tab-ledger">Central Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">

      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI icon={<Users className="w-4 h-4 text-blue-500" />} label="Total Users" value={core.total_users} />
        <KPI icon={<ShoppingCart className="w-4 h-4 text-green-500" />} label="Total Bookings" value={bk.total_bookings} />
        <KPI icon={<DollarSign className="w-4 h-4 text-emerald-500" />} label="Total GMV" value={`₱${fin.total_gmv.toLocaleString()}`} />
        <KPI icon={<TrendingUp className="w-4 h-4 text-teal-500" />} label="Platform Revenue" value={`₱${fin.total_platform_revenue.toLocaleString()}`} />
        <KPI icon={<BarChart3 className="w-4 h-4 text-purple-500" />} label="Take Rate" value={`${fin.take_rate}%`} />
        <KPI icon={<Users className="w-4 h-4 text-cyan-500" />} label="Conversion" value={`${core.conversion_rate}%`} />
      </div>

      {/* Financial Detail Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Monthly GMV</p>
            <p className="text-2xl font-heading font-bold">₱{fin.monthly_gmv.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">Platform fees: ₱{fin.monthly_platform_revenue.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Bookings (30d / 7d)</p>
            <p className="text-2xl font-heading font-bold">{bk.bookings_30d} / {bk.bookings_7d}</p>
            <p className="text-xs text-muted-foreground mt-1">Cancel rate: {bk.cancel_rate}%</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Growth (30d / 7d)</p>
            <p className="text-2xl font-heading font-bold">{core.new_users_30d} / {core.new_users_7d}</p>
            <p className="text-xs text-muted-foreground mt-1">New signups</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily Trend */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Daily Bookings (14d)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="bookings" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Daily GMV */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Daily GMV (14d)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="gmv" fill="#0d9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Revenue by Channel */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Revenue by Payment Channel</CardTitle></CardHeader>
          <CardContent>
            {data.revenue_by_channel.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.revenue_by_channel} dataKey="gmv" nameKey="channel" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ₱${value}`}>
                    {data.revenue_by_channel.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-muted-foreground py-8">No data yet</p>}
          </CardContent>
        </Card>

        {/* Revenue by Sport */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Revenue by Sport</CardTitle></CardHeader>
          <CardContent>
            {data.revenue_by_sport.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.revenue_by_sport} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="sport" type="category" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="gmv" fill="#059669" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-muted-foreground py-8">No data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Supply & Growth Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Supply Metrics</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div><p className="text-lg font-bold">{supply.total_venues}</p><p className="text-xs text-muted-foreground">Total Venues</p></div>
            <div><p className="text-lg font-bold">{supply.active_venues}</p><p className="text-xs text-muted-foreground">Active Venues</p></div>
            <div><p className="text-lg font-bold">{supply.total_coaches}</p><p className="text-xs text-muted-foreground">Coaches</p></div>
            <div><p className="text-lg font-bold">{supply.total_sessions}</p><p className="text-xs text-muted-foreground">Sessions</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Growth & Health</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div><p className="text-lg font-bold">{growth.total_ambassadors}</p><p className="text-xs text-muted-foreground">Ambassadors</p></div>
            <div><p className="text-lg font-bold">{growth.founding_members}/{1000 - growth.founding_remaining + growth.founding_members}</p><p className="text-xs text-muted-foreground">Founders</p></div>
            <div><p className="text-lg font-bold">{health.open_disputes}</p><p className="text-xs text-muted-foreground">Open Disputes</p></div>
            <div><p className="text-lg font-bold">{health.pending_fraud_flags}</p><p className="text-xs text-muted-foreground">Fraud Flags</p></div>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="finance" className="space-y-6" data-testid="finance-console-tab">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} label="Urgent Pending" value={financeData?.summary?.urgent_pending || 0} />
            <KPI icon={<DollarSign className="w-4 h-4 text-court-orange" />} label="Manual Adjustments" value={financeData?.summary?.manual_adjustments || 0} />
            <KPI icon={<ArrowDownRight className="w-4 h-4 text-red-500" />} label="Settlement Overrides" value={financeData?.summary?.settlement_overrides || 0} />
            <KPI icon={<ArrowUpRight className="w-4 h-4 text-green-500" />} label="Ready Settlements" value={financeData?.summary?.ready_settlements || 0} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Urgent Payout Requests</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Textarea value={reviewReason} onChange={(e) => setReviewReason(e.target.value)} placeholder="Optional review notes" data-testid="finance-review-reason" />
                {(financeData?.urgent_requests || []).slice(0, 8).map((req) => (
                  <div key={req.request_id} className="rounded-lg border border-slate-200 p-3" data-testid={`finance-urgent-${req.request_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm">{req.booking_id}</p>
                        <p className="text-xs text-muted-foreground">{req.reason || "No reason provided"}</p>
                      </div>
                      <Badge variant="outline" className="uppercase text-[10px]">{req.status}</Badge>
                    </div>
                    {req.status === "pending" && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={() => reviewUrgentRequest(req.request_id, "approve")} data-testid={`approve-urgent-${req.request_id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => reviewUrgentRequest(req.request_id, "reject")} data-testid={`reject-urgent-${req.request_id}`}>Reject</Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Manual Adjustments</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Target Type</Label>
                  <Select value={adjustTargetType} onValueChange={setAdjustTargetType}>
                    <SelectTrigger data-testid="adjust-target-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="settlement">Settlement / Booking</SelectItem>
                      <SelectItem value="payout_batch">Payout Batch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {adjustTargetType === "settlement" ? <Input value={adjustBookingId} onChange={(e) => setAdjustBookingId(e.target.value)} placeholder="Booking ID" data-testid="adjust-booking-id" /> : <Input value={adjustBatchId} onChange={(e) => setAdjustBatchId(e.target.value)} placeholder="Payout Batch ID" data-testid="adjust-batch-id" />}
                <div>
                  <Label className="text-xs">Adjustment Type</Label>
                  <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                    <SelectTrigger data-testid="adjustment-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deduct">Deduct</SelectItem>
                      <SelectItem value="add">Add Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} placeholder="Amount" type="number" data-testid="adjust-amount" />
                <Textarea value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder="Reason" data-testid="adjust-reason" />
                <Button onClick={submitAdjustment} disabled={submitting} className="w-full" data-testid="submit-adjustment-btn">Record Adjustment</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Settlement Override</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Input value={overrideBookingId} onChange={(e) => setOverrideBookingId(e.target.value)} placeholder="Booking ID" data-testid="override-booking-id" />
                <Select value={overrideStatus} onValueChange={setOverrideStatus}>
                  <SelectTrigger data-testid="override-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['on_hold','ready_for_payout','paid','failed','reversed','offset'].map((status) => <SelectItem key={status} value={status}>{status.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Reason for override" data-testid="override-reason" />
                <Button onClick={submitOverride} disabled={submitting || !overrideBookingId} className="w-full" data-testid="submit-override-btn">Save Override</Button>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Create Payout Batch</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-lg border border-slate-200 p-3 text-sm">
                  <p className="font-medium">Selected settlements</p>
                  <p className="text-2xl font-heading font-bold mt-1">{selectedSettlements.length}</p>
                </div>
                <Textarea value={batchNotes} onChange={(e) => setBatchNotes(e.target.value)} placeholder="Batch notes / cut-off description" data-testid="batch-notes-input" />
                <Button onClick={createBatch} disabled={submitting || selectedSettlements.length === 0} className="w-full" data-testid="create-batch-btn">Create Payout Batch</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Batch Execution Workflow</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Select value={selectedBatchId} onValueChange={setSelectedBatchId}>
                  <SelectTrigger data-testid="selected-batch-id"><SelectValue placeholder="Choose payout batch" /></SelectTrigger>
                  <SelectContent>
                    {(financeData?.payout_batches || []).map((batch) => <SelectItem key={batch.batch_id || batch.payout_batch_id} value={batch.batch_id || batch.payout_batch_id}>{batch.batch_id || batch.payout_batch_id}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={batchStatus} onValueChange={setBatchStatus}>
                  <SelectTrigger data-testid="batch-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['draft','approved','processing','completed','failed','reversed'].map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea value={batchStatusReason} onChange={(e) => setBatchStatusReason(e.target.value)} placeholder="Execution note / status reason" data-testid="batch-status-reason" />
                <Button onClick={updateBatchStatus} disabled={submitting || !selectedBatchId} className="w-full" data-testid="update-batch-status-btn">Update Batch Status</Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Settlement Queue</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[950px] text-sm" data-testid="finance-settlement-table">
                  <thead>
                    <tr className="border-b text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      <th className="py-3 pr-3">Select</th>
                      <th className="py-3 pr-3">Booking</th>
                      <th className="py-3 pr-3">Venue</th>
                      <th className="py-3 pr-3">Gross</th>
                      <th className="py-3 pr-3">Commission</th>
                      <th className="py-3 pr-3">Net</th>
                      <th className="py-3 pr-3">Status</th>
                      <th className="py-3">Hold Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(financeData?.settlements || []).slice(0, 120).map((settlement) => (
                      <tr key={settlement.settlement_id} className="border-b border-slate-100" data-testid={`finance-settlement-row-${settlement.booking_id}`}>
                        <td className="py-3 pr-3"><input type="checkbox" checked={selectedSettlements.includes(settlement.booking_id)} onChange={() => toggleSettlement(settlement.booking_id)} data-testid={`select-settlement-${settlement.booking_id}`} /></td>
                        <td className="py-3 pr-3">{settlement.booking_id}</td>
                        <td className="py-3 pr-3">{settlement.venue_name}</td>
                        <td className="py-3 pr-3">₱{settlement.gross_amount?.toLocaleString()}</td>
                        <td className="py-3 pr-3">₱{settlement.commission_amount?.toLocaleString()}</td>
                        <td className="py-3 pr-3">₱{settlement.net_payout?.toLocaleString()}</td>
                        <td className="py-3 pr-3"><Badge variant="outline">{settlement.payout_status}</Badge></td>
                        <td className="py-3 text-xs text-muted-foreground">{settlement.hold_reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Payout Batches</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(financeData?.payout_batches || []).map((batch) => {
                const id = batch.batch_id || batch.payout_batch_id;
                return (
                  <div key={id} className="rounded-lg border border-slate-200 p-3" data-testid={`finance-batch-${id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                      <div>
                        <p className="font-medium">{id}</p>
                        <p className="text-xs text-muted-foreground">{batch.settlements_count} settlements · Gross ₱{Number(batch.gross_amount || 0).toLocaleString()} · Net ₱{Number(batch.net_payout || 0).toLocaleString()}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="uppercase text-[10px]">{batch.status || batch.batch_status}</Badge>
                        <Button size="sm" variant="outline" onClick={() => exportBatch(id, 'json')} data-testid={`export-batch-json-${id}`}>JSON</Button>
                        <Button size="sm" variant="outline" onClick={() => exportBatch(id, 'csv')} data-testid={`export-batch-csv-${id}`}>Transfer CSV</Button>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/admin/finance/batches/${id}/report`)} data-testid={`export-batch-pdf-${id}`}>PDF Summary</Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Finance Audit Trail</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(financeData?.audit_trail || []).map((log) => (
                <div key={log.log_id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`finance-audit-${log.log_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">{log.action}</p>
                      <p className="text-xs text-muted-foreground">{log.entity_type} · {log.entity_id}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{log.timestamp?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="revenue-model" className="space-y-6" data-testid="revenue-model-tab">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <KPI icon={<TrendingUp className="w-4 h-4 text-court-orange" />} label="Commission Rules" value={revenueModelData?.summary?.commission_rules?.count || 0} />
            <KPI icon={<Repeat className="w-4 h-4 text-purple-500" />} label="Subscription Fees" value={revenueModelData?.summary?.subscription_fees?.count || 0} />
            <KPI icon={<Award className="w-4 h-4 text-blue-500" />} label="Special Rates" value={revenueModelData?.summary?.special_rates?.count || 0} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Booking Commission Rules</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Select value={commissionForm.target_type} onValueChange={(value) => setCommissionForm({ ...commissionForm, target_type: value })}><SelectTrigger data-testid="commission-target-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="venue">Venue</SelectItem><SelectItem value="coach_sp">Coach / SP</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
                <Input value={commissionForm.segment} onChange={(e) => setCommissionForm({ ...commissionForm, segment: e.target.value })} placeholder="Segment (all / school / lgu / etc.)" data-testid="commission-segment" />
                <Input value={commissionForm.tier} onChange={(e) => setCommissionForm({ ...commissionForm, tier: e.target.value })} placeholder="Tier" data-testid="commission-tier" />
                <Input type="number" value={commissionForm.commission_rate} onChange={(e) => setCommissionForm({ ...commissionForm, commission_rate: e.target.value })} placeholder="Commission rate %" data-testid="commission-rate" />
                <Textarea value={commissionForm.notes} onChange={(e) => setCommissionForm({ ...commissionForm, notes: e.target.value })} placeholder="Notes" data-testid="commission-notes" />
                <Button onClick={createCommissionRule} className="w-full" data-testid="save-commission-rule-btn">Save Commission Rule</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Subscription Fee Matrix</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Select value={subscriptionForm.target_type} onValueChange={(value) => setSubscriptionForm({ ...subscriptionForm, target_type: value })}><SelectTrigger data-testid="subscription-target-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="venue">Venue</SelectItem><SelectItem value="coach_sp">Coach / SP</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
                <Input value={subscriptionForm.tier} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, tier: e.target.value })} placeholder="Tier" data-testid="subscription-tier" />
                <Select value={subscriptionForm.billing_cycle} onValueChange={(value) => setSubscriptionForm({ ...subscriptionForm, billing_cycle: value })}><SelectTrigger data-testid="subscription-billing-cycle"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent></Select>
                <Input type="number" value={subscriptionForm.subscription_fee} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_fee: e.target.value })} placeholder="Subscription fee" data-testid="subscription-fee" />
                <Textarea value={subscriptionForm.notes} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, notes: e.target.value })} placeholder="Notes" data-testid="subscription-notes" />
                <Button onClick={createSubscriptionRule} className="w-full" data-testid="save-subscription-rule-btn">Save Subscription Fee</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Special Rates / Discounts</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Select value={specialRateForm.target_type} onValueChange={(value) => setSpecialRateForm({ ...specialRateForm, target_type: value })}><SelectTrigger data-testid="special-target-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="venue">Venue</SelectItem><SelectItem value="coach_sp">Coach / SP</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
                <Input value={specialRateForm.segment} onChange={(e) => setSpecialRateForm({ ...specialRateForm, segment: e.target.value })} placeholder="Segment" data-testid="special-segment" />
                <Input value={specialRateForm.tier} onChange={(e) => setSpecialRateForm({ ...specialRateForm, tier: e.target.value })} placeholder="Tier" data-testid="special-tier" />
                <Input value={specialRateForm.promotion_code} onChange={(e) => setSpecialRateForm({ ...specialRateForm, promotion_code: e.target.value })} placeholder="Promo code" data-testid="special-promo-code" />
                <Input type="number" value={specialRateForm.commission_rate_override} onChange={(e) => setSpecialRateForm({ ...specialRateForm, commission_rate_override: e.target.value })} placeholder="Commission override %" data-testid="special-commission-override" />
                <Input type="number" value={specialRateForm.subscription_discount_pct} onChange={(e) => setSpecialRateForm({ ...specialRateForm, subscription_discount_pct: e.target.value })} placeholder="Subscription discount %" data-testid="special-subscription-discount" />
                <Textarea value={specialRateForm.notes} onChange={(e) => setSpecialRateForm({ ...specialRateForm, notes: e.target.value })} placeholder="Notes" data-testid="special-notes" />
                <Button onClick={createSpecialRate} className="w-full" data-testid="save-special-rate-btn">Save Special Rate</Button>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <RuleTable title="Commission Rules" rows={revenueModelData?.commission_rules || []} idKey="rule_id" primaryField="target_type" secondaryField="commission_rate" secondaryLabel="commission" />
            <RuleTable title="Subscription Fees" rows={revenueModelData?.subscription_fees || []} idKey="fee_id" primaryField="target_type" secondaryField="subscription_fee" secondaryLabel="fee" />
            <RuleTable title="Special Rates" rows={revenueModelData?.special_rates || []} idKey="override_id" primaryField="promotion_code" secondaryField="subscription_discount_pct" secondaryLabel="discount %" />
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="space-y-6" data-testid="central-ledger-tab">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl">CFO Central Ledger</h2>
              <p className="text-sm text-muted-foreground">Traceability of all revenue collected across commissions, subscriptions, and related accounting entries.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportLedgerCsv} data-testid="export-ledger-csv-btn"><Download className="w-4 h-4 mr-2" />CSV</Button>
              <Button variant="outline" onClick={printLedgerPdf} data-testid="export-ledger-pdf-btn"><Download className="w-4 h-4 mr-2" />PDF</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KPI icon={<DollarSign className="w-4 h-4 text-court-orange" />} label="Ledger Entries" value={ledgerData?.summary?.entries || 0} />
            <KPI icon={<TrendingUp className="w-4 h-4 text-green-600" />} label="Total Revenue Collected" value={`₱${Number(ledgerData?.summary?.total_collected || 0).toLocaleString()}`} />
          </div>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Ledger Entries</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px] text-sm" data-testid="central-ledger-table">
                  <thead>
                    <tr className="border-b text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      <th className="py-3 pr-3">Type</th>
                      <th className="py-3 pr-3">Reference</th>
                      <th className="py-3 pr-3">Source</th>
                      <th className="py-3 pr-3">Segment / Tier</th>
                      <th className="py-3 pr-3">Gross</th>
                      <th className="py-3 pr-3">Commission</th>
                      <th className="py-3 pr-3">Subscription</th>
                      <th className="py-3 pr-3">Net Revenue</th>
                      <th className="py-3">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ledgerData?.entries || []).slice(0, 200).map((entry) => (
                      <tr key={entry.ledger_id} className="border-b border-slate-100" data-testid={`ledger-entry-${entry.ledger_id}`}>
                        <td className="py-3 pr-3">{entry.entry_type}</td>
                        <td className="py-3 pr-3">{entry.reference_id}</td>
                        <td className="py-3 pr-3">{entry.collected_from}</td>
                        <td className="py-3 pr-3">{entry.segment} / {entry.tier}</td>
                        <td className="py-3 pr-3">₱{Number(entry.gross_amount || 0).toLocaleString()}</td>
                        <td className="py-3 pr-3">₱{Number(entry.commission_amount || 0).toLocaleString()}</td>
                        <td className="py-3 pr-3">₱{Number(entry.subscription_fee || 0).toLocaleString()}</td>
                        <td className="py-3 pr-3 font-medium text-green-700">₱{Number(entry.net_revenue_collected || 0).toLocaleString()}</td>
                        <td className="py-3">{entry.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RuleTable({ title, rows, idKey, primaryField, secondaryField, secondaryLabel }) {
  return (
    <Card>
      <CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">No rules configured yet.</p> : rows.slice(0, 12).map((row) => (
          <div key={row[idKey]} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${row[idKey]}`}>
            <div className="flex items-center justify-between gap-3">
              <p className="font-medium">{row[primaryField] || 'default'}</p>
              <Badge variant="outline">{row.status}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{secondaryLabel}: {row[secondaryField]}</p>
            <p className="text-xs text-muted-foreground">segment: {row.segment || 'all'} · tier: {row.tier || 'all'}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function KPI({ icon, label, value }) {
  return (
    <Card>
      <CardContent className="p-3 text-center">
        <div className="flex justify-center mb-1">{icon}</div>
        <p className="text-lg font-heading font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
